import { stripAnchors } from "../../lib/stripAnchors";

// The pick an option renders (Review's parsed_picks entry, normalized).
export type AnswerPick = {
  label: string;
  value: string;
  description: string;
  suggested: boolean;
  pickId: string;
};

// AnswerOption is one proposed answer on a question card (0051 S1a): one click
// selects it, and nothing opens. It shows the option's label, its whole
// proposed wording (the text Save & next sends) and its one-sentence cost, so
// the founder judges the full proposal without opening anything. No card-level
// Enter shortcut selects it (a focused option activates on Enter or Space like
// any button); the card's Ctrl/Cmd+Enter is Save & next.
//
// The "recommended" badge is a marker, never a selection (0168 FD-4): a
// suggested option looks like every other one until it is selected, and a
// routing pick (one carrying a pick id, such as the delegation) is never
// marked recommended. The badge sits above the label, inside the text column,
// so every option's text has the same width. Only `selected` changes the
// border and fills the dot, as on the authority fork's rows. Every text span
// wraps: values run long.
export function AnswerOption({
  pick,
  selected,
  disabled,
  onSelect,
}: {
  pick: AnswerPick;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const label = stripAnchors(pick.label);
  const proposal = stripAnchors(pick.value);
  const cost = stripAnchors(pick.description);
  const showProposal = proposal.trim() !== "" && proposal.trim() !== label.trim();
  const recommended = pick.suggested && pick.pickId === "";
  const border = selected
    ? "border-accent ring-2 ring-accent/60"
    : "border-ink-700 hover:border-ink-500";
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={`group w-full text-left rounded-lg border ${border} bg-ink-900/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-start gap-4 px-5 py-4">
        <span
          className={`mt-1 inline-block h-3.5 w-3.5 rounded-full border shrink-0 ${
            selected ? "border-accent bg-accent" : "border-ink-500"
          }`}
        />
        <span className="flex-1 min-w-0">
          {recommended && (
            <span
              data-testid="answer-option-suggested"
              className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-accent"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
              recommended
            </span>
          )}
          <span className="block break-words text-[14px] leading-relaxed text-ink-100">
            {label}
          </span>
          {showProposal && (
            <span
              data-testid="answer-option-proposal"
              className="block mt-1 break-words text-[13px] leading-relaxed text-ink-300"
            >
              {proposal}
            </span>
          )}
          {cost.trim() !== "" && (
            <span
              data-testid="answer-option-cost"
              className="block mt-1 break-words text-[12px] leading-relaxed text-ink-500"
            >
              {cost}
            </span>
          )}
        </span>
      </div>
    </button>
  );
}
